import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  EXPORT_FORMATS,
  EXPORT_METADATA_COLUMNS,
  type ExportFormat,
  type ExportMetadataColumn,
  type SubmissionFilter,
} from "@sga/shared";
import { apiFetch } from "../lib/api.js";
import type { ColumnDefinition } from "../submissions/useSubmissions.js";

/**
 * The export dialog on the submissions table — docs/01-PRODUCT-SPEC.md §9.3.
 *
 * It exports *the current filter*, which is the only thing anyone asks for: the filters and view
 * shown on screen are passed straight through, so what comes out of the file is what was on the
 * table. Nothing is re-derived here.
 *
 * A caller without `view_pii` gets the redaction checkbox forced on and disabled, with the reason
 * stated. The server rejects the alternative with `PII_NOT_PERMITTED` regardless — this is so the
 * refusal is not a surprise.
 */

const FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: "CSV (opens in Excel)",
  xlsx: "Excel workbook (.xlsx)",
  json: "JSON Lines",
  pdf: "PDF, one page per submission",
};

interface Props {
  formId: string;
  columns: ColumnDefinition[];
  filters: SubmissionFilter[];
  viewId?: string;
  canViewPii: boolean;
  /** Encrypted fields are a superadmin disclosure, not a role permission — see the checkbox below. */
  isSuperadmin: boolean;
  onClose: () => void;
}

export const ExportDialog = ({
  formId,
  columns,
  filters,
  viewId,
  canViewPii,
  isSuperadmin,
  onClose,
}: Props): JSX.Element => {
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [selected, setSelected] = useState<string[]>([]);
  const [metadata, setMetadata] = useState<ExportMetadataColumn[]>([
    "reference",
    "submittedAt",
    "reviewStatus",
  ]);
  const [headerStyle, setHeaderStyle] = useState<"key" | "label">("key");
  const [labelRow, setLabelRow] = useState(false);
  const [useDisplayLabels, setUseDisplayLabels] = useState(false);
  const [flattenComposites, setFlattenComposites] = useState(true);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [redactPii, setRedactPii] = useState(!canViewPii);
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [sensitiveReason, setSensitiveReason] = useState("");
  const [queued, setQueued] = useState<string | null>(null);

  const sensitiveFields = columns.filter((column) => column.isSensitive);
  const sensitiveBlocked = includeSensitive && sensitiveReason.trim().length < 5;

  const run = useMutation({
    mutationFn: async () => {
      const body = {
        formId,
        format,
        filters,
        viewId,
        columns: selected,
        metadataColumns: metadata,
        headerStyle,
        labelRow,
        useDisplayLabels,
        flattenComposites,
        includeDrafts,
        redactPii,
        includeSensitive,
        sensitiveReason: includeSensitive ? sensitiveReason.trim() : undefined,
      };

      const response = await apiFetch<Response>("/exports", {
        method: "POST",
        body: JSON.stringify(body),
        // The inline path answers with the file itself, so this one request cannot go through the
        // JSON-parsing helper the rest of the app uses.
        rawResponse: true,
      });

      if (response.status === 202) {
        const job = (await response.json()) as { id: string; rowCount: number };
        setQueued(job.id);
        return;
      }

      // Under a thousand rows: the body *is* the file. Handed to the browser as a download rather
      // than routed through the export history, which would be a worse experience for the common
      // case of "give me these forty rows now".
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download =
        response.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ?? "export";
      anchor.click();
      URL.revokeObjectURL(url);
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-6"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Export submissions"
        className="flex max-h-[85vh] w-full max-w-lg flex-col gap-4 overflow-auto rounded-lg border bg-background p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Export submissions</h2>
        <p className="text-sm text-muted-foreground">
          Exports the {filters.length > 0 ? "current filter" : "whole form"}
          {viewId ? " from the selected view" : ""}.
        </p>

        <label className="flex flex-col gap-1 text-sm">
          Format
          <select
            value={format}
            onChange={(event) => setFormat(event.target.value as ExportFormat)}
            className="rounded-md border px-2 py-1.5"
          >
            {EXPORT_FORMATS.map((value) => (
              <option key={value} value={value}>
                {FORMAT_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="mb-1 font-medium">Columns</legend>
          <p className="text-xs text-muted-foreground">
            Nothing selected exports every field. Sensitive fields are never included.
          </p>
          <div className="max-h-40 overflow-auto rounded-md border p-2">
            {columns.map((column) => (
              <label key={column.key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected.includes(column.key)}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, column.key]
                        : current.filter((key) => key !== column.key),
                    )
                  }
                />
                {column.label}
                {column.isPii && <span className="text-xs text-muted-foreground">(personal)</span>}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="mb-1 font-medium">Metadata</legend>
          <div className="flex flex-wrap gap-x-4">
            {EXPORT_METADATA_COLUMNS.map((column) => (
              <label key={column} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={metadata.includes(column)}
                  onChange={(event) =>
                    setMetadata((current) =>
                      event.target.checked
                        ? [...current, column]
                        : current.filter((entry) => entry !== column),
                    )
                  }
                />
                {column}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="mb-1 font-medium">Formatting</legend>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={headerStyle === "label"}
              onChange={(event) => setHeaderStyle(event.target.checked ? "label" : "key")}
            />
            Use question wording as the header instead of the field key
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={labelRow}
              onChange={(event) => setLabelRow(event.target.checked)}
            />
            Add a second row with the question wording
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={useDisplayLabels}
              onChange={(event) => setUseDisplayLabels(event.target.checked)}
            />
            Export the displayed choice labels rather than stored values
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={flattenComposites}
              onChange={(event) => setFlattenComposites(event.target.checked)}
            />
            Split composite fields into one column per part
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeDrafts}
              onChange={(event) => setIncludeDrafts(event.target.checked)}
            />
            Include unfinished drafts
          </label>
          {includeDrafts && (
            <p className="pl-6 text-xs text-muted-foreground">
              Drafts have no reference number and no submitted date, and an applicant who drafted
              before submitting appears twice. The Submission status column tells them apart.
            </p>
          )}
        </fieldset>

        <label className="flex items-start gap-2 rounded-md bg-muted/40 p-2 text-sm">
          <input
            type="checkbox"
            checked={redactPii}
            disabled={!canViewPii || includeSensitive}
            onChange={(event) => setRedactPii(event.target.checked)}
          />
          <span>
            Redact personal data
            {!canViewPii && (
              <span className="block text-xs text-muted-foreground">
                Your role can only produce redacted exports.
              </span>
            )}
          </span>
        </label>

        {/* Only shown when the form actually has encrypted fields, and only to someone who could
            use it. An option that is always visible and almost always refused teaches people to
            ignore the warning attached to it. */}
        {isSuperadmin && sensitiveFields.length > 0 && (
          <div className="rounded-md border border-brand-red/40 bg-brand-red/5 p-2 text-sm">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={includeSensitive}
                onChange={(event) => {
                  setIncludeSensitive(event.target.checked);
                  // The two are mutually exclusive server-side; reflecting that here stops a
                  // request being built that the API would only reject.
                  if (event.target.checked) setRedactPii(false);
                }}
              />
              <span>
                <span className="font-medium">Include encrypted fields</span>
                <span className="block text-xs text-muted-foreground">
                  {sensitiveFields.map((field) => field.label).join(", ")} — decrypted, in plain
                  text, for every row in this export. Recorded against your name.
                </span>
              </span>
            </label>

            {includeSensitive && (
              <div className="mt-2 pl-6">
                <input
                  aria-label="Why these fields are needed"
                  value={sensitiveReason}
                  onChange={(event) => setSensitiveReason(event.target.value)}
                  placeholder="Why are these needed? e.g. stipend disbursement, October cycle"
                  className="w-full rounded-md border px-2 py-1 text-sm"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  The file expires after seven days and only you can download it.
                </p>
              </div>
            )}
          </div>
        )}

        {queued && (
          <p role="status" className="rounded-md bg-accent/20 p-2 text-sm text-primary">
            This export is too large to download directly, so it is being generated in the
            background. It will appear in your export history, and you will get an email when it is
            ready.
          </p>
        )}

        {run.isError && (
          <p role="alert" className="rounded-md bg-brand-red/10 p-2 text-sm text-brand-red">
            {(run.error as Error).message}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border px-3 py-1.5 text-sm">
            {queued ? "Close" : "Cancel"}
          </button>
          {!queued && (
            <button
              type="button"
              onClick={() => run.mutate()}
              disabled={run.isPending || sensitiveBlocked}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-60"
            >
              {run.isPending ? "Preparing…" : "Export"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
